/**
 * useAutoSave - Hook for message auto-save / draft functionality
 *
 * Handles:
 * - Periodic auto-save via WebSocket
 * - Restore draft on mount
 * - Clear draft on send
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useWebSocketContext } from 'context/WebSocketConnectionContext';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { getAutoSaveDraft } from '../services/messageService';
import { messageQueryKeys } from '../config/queryKeys';
import { AUTO_SAVE_INTERVAL_MS } from '../constants';

/**
 * @param {Object} options
 * @param {string} options.caseId - Current case ID
 * @param {string} options.autoSaveType - 'thread' | 'reply'
 * @param {string} options.messageThreadId - Thread ID (for reply auto-save)
 * @param {Function} options.getFormValues - Function to get current form values
 * @param {boolean} options.isDirty - Whether the form has unsaved changes
 * @param {boolean} options.enabled - Enable/disable auto-save
 * @returns {{ draftData, hasDraft, clearDraft }}
 */
export function useAutoSave({
  caseId = '',
  autoSaveType = 'thread',
  messageThreadId = '',
  getFormValues,
  isDirty = false,
  enabled = true,
} = {}) {
  const { sendMessage } = useWebSocketContext();
  const { firmDetails } = useUserDetailsContext();
  const intervalRef = useRef(null);
  const [hasDraft, setHasDraft] = useState(false);

  // Fetch existing draft
  const { data: draftData } = useQuery({
    queryKey: messageQueryKeys.autoSave.byCaseId(caseId),
    queryFn: () => getAutoSaveDraft(caseId),
    enabled: enabled && autoSaveType === 'thread',
    staleTime: 0, // Always fresh
    gcTime: 0,
    refetchOnWindowFocus: false,
    select: (response) => response?.data || null,
  });

  useEffect(() => {
    if (draftData && Object.keys(draftData).length > 0) {
      setHasDraft(true);
    }
  }, [draftData]);

  // Auto-save function
  const saveToServer = useCallback(
    async (formData, isDelete = false) => {
      if (!sendMessage) return;

      try {
        const payload = {
          user_id: firmDetails?.current_user,
          tenant_id: firmDetails?.tenant_id,
          auto_save_type: autoSaveType,
          action: 'autosave',
          type: autoSaveType === 'thread' ? 'autosave_message' : 'autosave_message_reply',
          is_delete: isDelete,
          case_id: caseId,
          data: formData,
        };

        if (autoSaveType === 'reply' && messageThreadId) {
          payload.message_thread_id = messageThreadId;
        }
        if (firmDetails?.firm_user_id) payload.firm_user_id = firmDetails.firm_user_id;
        if (firmDetails?.contact_id) payload.contact_id = firmDetails.contact_id;

        await sendMessage(payload);
      } catch (err) {
        console.error('Auto-save failed:', err);
      }
    },
    [sendMessage, firmDetails, autoSaveType, caseId, messageThreadId]
  );

  // Periodic auto-save
  useEffect(() => {
    if (!enabled || !getFormValues) return;

    intervalRef.current = setInterval(() => {
      if (isDirty) {
        saveToServer(getFormValues());
      }
    }, AUTO_SAVE_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, isDirty, getFormValues, saveToServer]);

  // Clear draft
  const clearDraft = useCallback(() => {
    saveToServer(null, true);
    setHasDraft(false);
  }, [saveToServer]);

  // Manual save
  const saveDraft = useCallback(
    (formData) => {
      saveToServer(formData);
    },
    [saveToServer]
  );

  return {
    draftData,
    hasDraft,
    clearDraft,
    saveDraft,
  };
}
